<div class="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
  <div class="max-w-3xl mx-auto">

    <!-- Loading -->
    @if (loading) {
      <div class="py-20 text-center text-gray-500 dark:text-gray-400">Cargando cotización...</div>
    }

    <!-- 404 / no disponible -->
    @else if (notFound) {
      <div class="rounded-xl bg-white dark:bg-gray-800 shadow p-8 text-center space-y-3">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-error-100 text-error-600 dark:bg-error-500/10">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <p class="text-lg font-medium text-gray-900 dark:text-white">Cotización no disponible o expirada</p>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          El enlace no es válido, fue revocado o la cotización ya no está disponible.
          Si necesitas ayuda, contacta a la persona que te lo compartió.
        </p>
      </div>
    }

    <!-- 410 / versión reemplazada -->
    @else if (replaced) {
      <div class="rounded-xl bg-white dark:bg-gray-800 shadow p-8 text-center space-y-3">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
        </div>
        <p class="text-lg font-medium text-gray-900 dark:text-white">Esta cotización fue actualizada</p>
        <p class="text-sm text-gray-500 dark:text-gray-400">{{ replacedMessage }}</p>
      </div>
    }

    <!-- Cotización -->
    @else if (quote) {

      <!-- Banner: aceptada y firmada -->
      @if (isAccepted) {
        <div class="mb-4 rounded-xl border border-success-200 bg-success-50 dark:border-success-500/30 dark:bg-success-500/10 p-5 flex items-start gap-4">
          <div class="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-success-100 text-success-600 dark:bg-success-500/20 dark:text-success-400">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <p class="font-semibold text-success-700 dark:text-success-400">
              @if (justSigned) { ¡Gracias! Cotización aceptada } @else { Cotización aceptada }
            </p>
            <p class="text-sm text-success-700/90 dark:text-success-400/90 mt-0.5">
              Firmada por <span class="font-medium">{{ quote.signerName || 'el cliente' }}</span>
              @if (quote.signedAt) { el {{ formatFechaHora(quote.signedAt) }} }.
            </p>
            <p class="text-sm text-success-700/80 dark:text-success-400/80 mt-1">
              El PDF incluye la constancia de aceptación con la firma registrada.
            </p>
            <button type="button" (click)="descargarPdf()" [disabled]="downloading"
              class="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-success-600 hover:bg-success-700 transition-colors disabled:opacity-50">
              @if (downloading) {
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Generando...
              } @else {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Descargar PDF firmado
              }
            </button>
          </div>
        </div>
      }

      <!-- Banner: rechazada -->
      @else if (isRejected) {
        <div class="mb-4 rounded-xl border border-error-200 bg-error-50 dark:border-error-500/30 dark:bg-error-500/10 p-5 flex items-start gap-4">
          <div class="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-error-100 text-error-600 dark:bg-error-500/20 dark:text-error-400">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <p class="font-semibold text-error-700 dark:text-error-400">
              @if (justRejected) { Cotización rechazada — gracias por tu respuesta } @else { Cotización rechazada }
            </p>
            <p class="text-sm text-error-700/90 dark:text-error-400/90 mt-0.5">
              Motivo: <span class="font-medium">{{ rejectionReasonLabel }}</span>
            </p>
            @if (quote.rejectionComment) {
              <p class="text-sm text-error-700/80 dark:text-error-400/80 mt-1 italic whitespace-pre-line">"{{ quote.rejectionComment }}"</p>
            }
            <p class="text-sm text-error-700/70 dark:text-error-400/70 mt-1">
              Si cambias de opinión, contacta a tu asesor.
            </p>
          </div>
        </div>
      }

      <!-- Banner: expirada -->
      @else if (isExpired) {
        <div class="mb-4 rounded-xl border border-warning-200 bg-warning-50 dark:border-warning-500/30 dark:bg-warning-500/10 p-5 flex items-start gap-4">
          <div class="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-warning-100 text-warning-600 dark:bg-warning-500/20 dark:text-warning-400">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <p class="font-semibold text-warning-700 dark:text-warning-400">Cotización expirada</p>
            <p class="text-sm text-warning-700/80 dark:text-warning-400/80 mt-0.5">
              El período de validez de esta cotización terminó. Si sigues interesado, contacta a la persona que te la envió para solicitar una nueva versión.
            </p>
          </div>
        </div>
      }

      <!-- Barra de acciones (CTA) -->
      @if (canSign || canReject) {
        <div class="mb-4 rounded-xl bg-white dark:bg-gray-800 shadow p-5 border-l-4 sm:flex items-center justify-between gap-4"
          [style.border-left-color]="accentColor">
          <div class="min-w-0">
            <p class="font-semibold text-gray-900 dark:text-white">Esta cotización está pendiente de tu decisión</p>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Puedes aceptarla firmando electrónicamente o rechazarla indicando el motivo.
            </p>
          </div>
          <div class="flex flex-col-reverse xsm:flex-row gap-2 xsm:gap-3 mt-4 sm:mt-0 shrink-0">
            @if (canReject) {
              <button type="button" (click)="abrirRechazo()"
                class="px-5 py-2.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                Rechazar
              </button>
            }
            @if (canSign) {
              <button type="button" (click)="abrirFirma()"
                class="px-5 py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                [style.background-color]="accentColor">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
                Aceptar y firmar
              </button>
            }
          </div>
        </div>
      }

      <div class="rounded-xl bg-white dark:bg-gray-800 shadow overflow-hidden border-t-4"
        [style.border-top-color]="accentColor">

        <!-- ============ Documento legal (componente compartido) ============ -->
        @if (docData) {
          <div class="p-6 sm:p-8">
            <app-quote-document [data]="docData"></app-quote-document>
          </div>
        }

        <!-- Documentos adjuntos (solo si hay) -->
        @if (attachments.length > 0) {
          <div class="px-6 sm:px-8 pb-2 border-t border-gray-200 dark:border-gray-700 pt-5">
            <h3 class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Documentos</h3>
            <ul class="space-y-2 mb-4">
              @for (att of attachments; track att.id) {
                <li class="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
                  <app-quote-attachment-icon [mimeType]="att.mimeType" [fileName]="att.fileName"></app-quote-attachment-icon>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate" [title]="att.fileName">{{ att.fileName }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ attachmentSize(att) }}</p>
                  </div>
                  <button type="button" (click)="descargarAdjunto(att)" [disabled]="isAttachmentDownloading(att)"
                    class="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                    [style.color]="accentColor">
                    @if (isAttachmentDownloading(att)) {
                      <div class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current"></div>
                    } @else {
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2"/>
                      </svg>
                    }
                    Descargar
                  </button>
                </li>
              }
            </ul>
          </div>
        }

        <!-- Acciones -->
        <div class="px-6 sm:px-8 py-5 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse xsm:flex-row justify-end gap-2 xsm:gap-3">
          <button type="button" (click)="descargarPdf()" [disabled]="downloading"
            class="px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            [class]="canSign
              ? 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              : 'text-white'"
            [style.background-color]="canSign ? null : accentColor">
            @if (downloading) {
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
              Generando...
            } @else {
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              {{ isAccepted ? 'Descargar PDF firmado' : 'Descargar PDF' }}
            }
          </button>
          @if (canSign) {
            <button type="button" (click)="abrirFirma()"
              class="px-5 py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              [style.background-color]="accentColor">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              Aceptar y firmar
            </button>
          }
        </div>
      </div>

      <p class="text-center text-xs text-gray-400 mt-4">
        Cotización generada electrónicamente.
      </p>
    }
  </div>
</div>

<!-- ============ Modal: Aceptar y firmar ============ -->
@if (showSignModal) {
  <div class="fixed inset-0 z-modal flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/50" (click)="cerrarFirma()"></div>
    <div class="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">

      <!-- Header -->
      <div class="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Aceptar y firmar</h3>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {{ quote?.quoteNumber }} · Total {{ currencySymbol }} {{ quote?.totalAmount | number:'1.2-2' }}
          </p>
        </div>
        <button type="button" (click)="cerrarFirma()" [disabled]="signing"
          class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Body -->
      <form [formGroup]="signForm" (ngSubmit)="firmar()" class="flex-1 overflow-y-auto px-6 py-4 space-y-4">

        <!-- Tabs de método de firma -->
        <div>
          <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Método de firma</p>
          <div class="grid grid-cols-3 gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800">
            <button type="button" (click)="setSignTab('drawn')"
              class="px-2 py-1.5 rounded-md text-sm font-medium transition-colors"
              [class]="signTab === 'drawn'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
              Dibujar
            </button>
            <button type="button" (click)="setSignTab('typed')"
              class="px-2 py-1.5 rounded-md text-sm font-medium transition-colors"
              [class]="signTab === 'typed'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
              Escribir
            </button>
            <button type="button" (click)="setSignTab('upload')"
              class="px-2 py-1.5 rounded-md text-sm font-medium transition-colors"
              [class]="signTab === 'upload'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
              Subir
            </button>
          </div>
        </div>

        <!-- Tab: Dibujar -->
        @if (signTab === 'drawn') {
          <div>
            <div class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-100 overflow-hidden">
              <canvas #signCanvas class="block w-full h-44 touch-none cursor-crosshair"
                (pointerdown)="onPointerDown($event)"
                (pointermove)="onPointerMove($event)"
                (pointerup)="onPointerUp($event)"
                (pointercancel)="onPointerUp($event)"></canvas>
            </div>
            <div class="flex items-center justify-between mt-1.5">
              <p class="text-xs text-gray-400 dark:text-gray-500">Dibuja tu firma dentro del recuadro.</p>
              <button type="button" (click)="limpiarFirma()" [disabled]="!hasDrawing"
                class="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-error-600 dark:hover:text-error-400 disabled:opacity-40 transition-colors">
                Limpiar
              </button>
            </div>
          </div>
        }

        <!-- Tab: Escribir -->
        @if (signTab === 'typed') {
          <div>
            <div class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 h-44 flex items-center justify-center px-4 bg-white dark:bg-gray-800">
              <span class="text-3xl text-gray-800 dark:text-white italic text-center break-words max-w-full"
                style="font-family: 'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive;">
                {{ typedPreview }}
              </span>
            </div>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
              Tu nombre completo (campo de abajo) se usará como firma tipográfica.
            </p>
          </div>
        }

        <!-- Tab: Subir -->
        @if (signTab === 'upload') {
          <div>
            @if (uploadedSignature) {
              <div class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 h-44 flex items-center justify-center bg-white dark:bg-gray-100 p-3">
                <img [src]="uploadedSignature" alt="Firma subida" class="max-h-full max-w-full object-contain">
              </div>
              <div class="flex justify-end mt-1.5">
                <button type="button" (click)="quitarImagenFirma()"
                  class="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-error-600 dark:hover:text-error-400 transition-colors">
                  Quitar imagen
                </button>
              </div>
            } @else {
              <label class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 h-44 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800">
                <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-300 font-medium">Selecciona una imagen de tu firma</span>
                <span class="text-xs text-gray-400">PNG o JPG, máximo 1 MB</span>
                <input type="file" accept="image/png,image/jpeg" class="hidden" (change)="onSignatureFileSelected($event)">
              </label>
            }
            @if (uploadError) {
              <p class="text-xs text-error-600 dark:text-error-400 mt-1.5">{{ uploadError }}</p>
            }
          </div>
        }

        <!-- Datos del firmante -->
        <div class="space-y-3">
          <div>
            <label for="sign-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nombre completo <span class="text-error-500">*</span>
            </label>
            <input id="sign-name" type="text" formControlName="signatoryName" placeholder="Ej. Juan Pérez"
              class="w-full px-3 py-2 rounded-lg border text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              [class]="signNameInvalid ? 'border-error-400 dark:border-error-500' : 'border-gray-300 dark:border-gray-600'">
            @if (signNameInvalid) {
              <p class="text-xs text-error-600 dark:text-error-400 mt-1">Ingresa tu nombre completo.</p>
            }
          </div>
          <div>
            <label for="sign-email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Correo electrónico <span class="text-error-500">*</span>
            </label>
            <input id="sign-email" type="email" formControlName="signatoryEmail" placeholder="Ej. juan@empresa.com"
              class="w-full px-3 py-2 rounded-lg border text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              [class]="signEmailInvalid ? 'border-error-400 dark:border-error-500' : 'border-gray-300 dark:border-gray-600'">
            @if (signEmailInvalid) {
              <p class="text-xs text-error-600 dark:text-error-400 mt-1">Ingresa un correo electrónico válido.</p>
            }
          </div>

          <label class="flex items-start gap-2.5 cursor-pointer select-none">
            <input type="checkbox" formControlName="consent"
              class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500/30">
            <span class="text-xs text-gray-600 dark:text-gray-300"
              [class.text-error-600]="signConsentInvalid">
              Acepto que esta firma electrónica tiene validez como manifestación de mi conformidad con esta cotización. <span class="text-error-500">*</span>
            </span>
          </label>
          @if (signConsentInvalid) {
            <p class="text-xs text-error-600 dark:text-error-400 -mt-1.5">Debes aceptar la declaración para continuar.</p>
          }
        </div>

        @if (signError) {
          <div class="rounded-lg bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 px-3 py-2">
            <p class="text-sm text-error-600 dark:text-error-400">{{ signError }}</p>
          </div>
        }
      </form>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse xsm:flex-row justify-end gap-2 xsm:gap-3">
        <button type="button" (click)="cerrarFirma()" [disabled]="signing"
          class="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50">
          Cancelar
        </button>
        <button type="button" (click)="firmar()" [disabled]="signing"
          class="px-5 py-2.5 rounded-lg text-sm font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          [style.background-color]="accentColor">
          @if (signing) {
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            Firmando...
          } @else {
            Firmar y aceptar
          }
        </button>
      </div>
    </div>
  </div>
}

<!-- ============ Modal: Rechazar cotización ============ -->
@if (showRejectModal) {
  <div class="fixed inset-0 z-modal flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/50" (click)="cerrarRechazo()"></div>
    <div class="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">

      <!-- Header -->
      <div class="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Rechazar cotización</h3>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ quote?.quoteNumber }}</p>
        </div>
        <button type="button" (click)="cerrarRechazo()" [disabled]="rejecting"
          class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      @if (rejectStep === 'form') {
        <!-- Paso 1: formulario -->
        <form [formGroup]="rejectForm" (ngSubmit)="continuarRechazo()" class="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          <div>
            <label for="reject-reason" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo <span class="text-error-500">*</span>
            </label>
            <select id="reject-reason" formControlName="reason"
              class="w-full px-3 py-2 rounded-lg border text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              [class]="rejectReasonInvalid ? 'border-error-400 dark:border-error-500' : 'border-gray-300 dark:border-gray-600'">
              <option value="" disabled>Selecciona un motivo...</option>
              @for (r of rejectionReasons; track r.value) {
                <option [value]="r.value">{{ r.label }}</option>
              }
            </select>
            @if (rejectReasonInvalid) {
              <p class="text-xs text-error-600 dark:text-error-400 mt-1">Selecciona un motivo de rechazo.</p>
            }
          </div>

          <div>
            <label for="reject-comment" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Comentario <span class="text-gray-400 font-normal">(opcional)</span>
            </label>
            <textarea id="reject-comment" formControlName="comment" rows="3" maxlength="2000"
              placeholder="Cuéntanos un poco más, nos ayuda a mejorar la propuesta..."
              class="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 resize-y"></textarea>
            <p class="text-xs text-gray-400 text-right">{{ rejectCommentLength }}/2000</p>
          </div>

          <div class="grid grid-cols-1 xsm:grid-cols-2 gap-3">
            <div>
              <label for="reject-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre <span class="text-gray-400 font-normal">(opcional)</span>
              </label>
              <input id="reject-name" type="text" formControlName="rejectorName" maxlength="200" placeholder="Ej. Juan Pérez"
                class="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
            </div>
            <div>
              <label for="reject-email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Correo <span class="text-gray-400 font-normal">(opcional)</span>
              </label>
              <input id="reject-email" type="email" formControlName="rejectorEmail" maxlength="255" placeholder="Ej. juan@empresa.com"
                class="w-full px-3 py-2 rounded-lg border text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                [class]="rejectEmailInvalid ? 'border-error-400 dark:border-error-500' : 'border-gray-300 dark:border-gray-600'">
              @if (rejectEmailInvalid) {
                <p class="text-xs text-error-600 dark:text-error-400 mt-1">Correo no válido.</p>
              }
            </div>
          </div>

          @if (rejectError) {
            <div class="rounded-lg bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 px-3 py-2">
              <p class="text-sm text-error-600 dark:text-error-400">{{ rejectError }}</p>
            </div>
          }
        </form>

        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse xsm:flex-row justify-end gap-2 xsm:gap-3">
          <button type="button" (click)="cerrarRechazo()"
            class="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
            Cancelar
          </button>
          <button type="button" (click)="continuarRechazo()"
            class="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-error-600 hover:bg-error-700 transition-colors">
            Continuar
          </button>
        </div>
      } @else {
        <!-- Paso 2: confirmación -->
        <div class="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div class="flex items-start gap-3">
            <div class="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-warning-100 text-warning-600 dark:bg-warning-500/15 dark:text-warning-400">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              </svg>
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-gray-900 dark:text-white">¿Confirmas el rechazo de esta cotización?</p>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Esta acción notificará al vendedor y la cotización quedará marcada como rechazada.
              </p>
            </div>
          </div>
          <div class="rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3 text-sm space-y-1">
            <p class="text-gray-700 dark:text-gray-300">
              <span class="text-gray-500 dark:text-gray-400">Motivo:</span>
              <span class="font-medium"> {{ selectedReasonLabel }}</span>
            </p>
            @if (rejectForm.get('comment')?.value) {
              <p class="text-gray-600 dark:text-gray-400 italic whitespace-pre-line">"{{ rejectForm.get('comment')?.value }}"</p>
            }
          </div>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse xsm:flex-row justify-end gap-2 xsm:gap-3">
          <button type="button" (click)="volverARechazoForm()" [disabled]="rejecting"
            class="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50">
            Volver
          </button>
          <button type="button" (click)="confirmarRechazo()" [disabled]="rejecting"
            class="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-error-600 hover:bg-error-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            @if (rejecting) {
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Enviando...
            } @else {
              Confirmar rechazo
            }
          </button>
        </div>
      }
    </div>
  </div>
}
